import type { AnimalSplitPart } from './processor-types';

/**
 * Canonical cut & wrap / additional-charge billing rules for V2 cutsheets.
 *
 * Both documents that bill a split — the Stripe invoice (built in the web
 * `generate-invoice` page) and the billing statement PDF (built in the API's
 * `calculateBillingStatementLineItems`) — resolve prices through the helpers
 * here so the same split can't be billed two different amounts. Before this
 * module the two paths implemented the order independently and disagreed
 * about which questionnaire to read.
 */

/**
 * The candidate $/lb figures for one split, most specific first.
 *
 * All fields are "whatever the caller could find" — `undefined`/`null`/`0`
 * all mean "not set", since the underlying fields are optional and a stored
 * `0` is indistinguishable from an empty numeric input in the builder.
 */
export interface CutWrapPriceSources {
  /**
   * The $/lb stamped on the questionnaire *response* — the price the customer
   * saw and agreed to when they submitted. Most specific, so it wins.
   */
  responsePricePerPound?: number | null;
  /**
   * The custom $/lb set on the questionnaire the response was built from.
   *
   * Read this from `questionnaireResponse.questionnaire` (the form actually
   * answered), not from `split.questionnaire` (the form currently assigned) —
   * the two diverge the moment a split is reassigned after submission.
   */
  questionnairePricePerPound?: number | null;
  /**
   * The processor's own `customCutsheetPricing` rate for this animal's
   * species + inspection level.
   */
  processorCustomRate?: number | null;
  /**
   * A legacy V1 vendor cutsheet's own `pricePerPound`. V2 splits have no
   * legacy cutsheet, so this is only reached by V1 vendor sheets.
   */
  legacyCutsheetPricePerPound?: number | null;
}

/** `true` when a candidate price is a usable positive rate. */
function isSetPrice(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Resolves the cut & wrap $/lb of hanging weight for a split.
 *
 * Resolution order — the price stamped on the response, then the form's own
 * custom $/lb, then the processor's inspection-level custom-cutsheet rate,
 * then a legacy V1 vendor cutsheet's price. Returns `0` when nothing is set,
 * which callers surface as a $0 line rather than dropping the charge.
 * @param sources The candidate prices for this split.
 */
export function resolveCutWrapPricePerPound(
  sources: CutWrapPriceSources,
): number {
  const {
    responsePricePerPound,
    questionnairePricePerPound,
    processorCustomRate,
    legacyCutsheetPricePerPound,
  } = sources;

  if (isSetPrice(responsePricePerPound)) return responsePricePerPound;
  if (isSetPrice(questionnairePricePerPound)) return questionnairePricePerPound;
  if (isSetPrice(processorCustomRate)) return processorCustomRate;
  if (isSetPrice(legacyCutsheetPricePerPound))
    return legacyCutsheetPricePerPound;

  return 0;
}

/** Each recognised split part's share of one animal. */
const SPLIT_PART_PORTIONS: Record<AnimalSplitPart, number> = {
  whole: 1,
  half: 0.5,
  quarter: 0.25,
};

/**
 * The share of a whole animal one split represents, for prorating a
 * `per_animal` charge.
 *
 * Throws on an unrecognised or missing `splitPart` rather than inheriting
 * `getSplitMultiplier`'s permissive `return 1`: that fallback bills a
 * per-animal fee **in full** on every split it doesn't recognise, and silent
 * over-billing is the worse failure direction. This mirrors `getSplitPortion`
 * in the API's `calculateInvoiceItems`, which already throws on the same input.
 * @param splitPart The split's portion of the animal.
 */
export function perAnimalSplitPortion(
  splitPart: string | null | undefined,
): number {
  const key = (splitPart ?? '').toLowerCase() as AnimalSplitPart;
  const portion = SPLIT_PART_PORTIONS[key];

  if (portion === undefined) {
    throw new Error(
      `Unable to prorate a per-animal charge: unrecognized animal split part "${splitPart}"`,
    );
  }

  return portion;
}

/**
 * What a questionnaire fee's `amount` is a price *of*.
 *
 * - `fixed` — a dollar amount, spread across the animal's shares by `basis`.
 * - `per_lb_live` — dollars per pound of the animal's live weight.
 * - `per_lb_hanging` — dollars per pound of hanging weight.
 */
export const FEE_PRICE_TYPES = [
  'fixed',
  'per_lb_live',
  'per_lb_hanging',
] as const;

export type FeePriceType = (typeof FEE_PRICE_TYPES)[number];

/**
 * How a `fixed` fee is spread across the shares of one animal.
 *
 * - `per_animal` — the amount covers the whole animal and is prorated by each
 *   share's portion, so a $100 fee on a head cut into halves bills $50 to each.
 * - `per_split` — every share on the animal is billed the full amount.
 */
export const FEE_BASES = ['per_animal', 'per_split'] as const;

export type FeeBasis = (typeof FEE_BASES)[number];

/**
 * A price authored on a questionnaire — the kill slot override, the processing
 * fee, or one additional charge. A missing `priceType` reads as `fixed` and a
 * missing `basis` as `per_animal`, which is what every fee stored before price
 * types existed meant.
 */
export interface QuestionnaireFee {
  amount: number;
  priceType?: string | null;
  basis?: string | null;
}

/** The weights a per-pound fee is billed on, for one share. */
export interface SplitFeeContext {
  /** The share's portion of the animal (`whole` / `half` / `quarter`). */
  splitPart: string | null | undefined;
  /**
   * This share's own hanging weight (already split out by the caller — see
   * `calculateSplitHangingWeight`), in pounds.
   */
  hangingWeight?: number | null;
  /**
   * The whole animal's live weight, in pounds. Live weight is only ever
   * recorded per head, so a share is billed on its portion of it.
   */
  liveWeight?: number | null;
}

/** One fee, resolved to the line a single share is billed. */
export interface ResolvedFee {
  /** Pounds for a per-pound fee; `1` for a fixed one. */
  quantity: number;
  /** $/lb for a per-pound fee; the share's prorated amount for a fixed one. */
  unitPrice: number;
  /** What the share owes — `quantity × unitPrice`, rounded to cents. */
  amount: number;
  /** The weight a per-pound fee was billed on; absent for a fixed fee. */
  weightBasis?: 'live' | 'hanging';
}

/** `true` when a value is a usable non-negative weight. */
function toWeight(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : 0;
}

/** Rounds to cents. */
function toCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Resolves one questionnaire fee into the line a single share is billed.
 *
 * A per-pound fee bills the share's own weight — its hanging weight, or its
 * portion of the animal's live weight — so per-animal vs per-split doesn't
 * apply to it. A missing weight resolves to quantity `0` rather than dropping
 * the fee: callers surface that as a $0 line the processor can see and fix
 * once the weight is recorded (the SUP-268 rule for the cut & wrap line).
 *
 * Throws on an unrecognised `splitPart` whenever the portion is needed (see
 * `perAnimalSplitPortion`).
 * @param fee The fee as authored on the questionnaire.
 * @param context The share being billed and its weights.
 */
export function resolveFeeForSplit(
  fee: QuestionnaireFee,
  context: SplitFeeContext,
): ResolvedFee {
  const rate = fee.amount;

  if (fee.priceType === 'per_lb_hanging') {
    const pounds = toCents(toWeight(context.hangingWeight));
    return {
      quantity: pounds,
      unitPrice: rate,
      amount: toCents(rate * pounds),
      weightBasis: 'hanging',
    };
  }

  if (fee.priceType === 'per_lb_live') {
    const pounds = toCents(
      toWeight(context.liveWeight) * perAnimalSplitPortion(context.splitPart),
    );
    return {
      quantity: pounds,
      unitPrice: rate,
      amount: toCents(rate * pounds),
      weightBasis: 'live',
    };
  }

  const multiplier =
    fee.basis === 'per_split' ? 1 : perAnimalSplitPortion(context.splitPart);
  const amount = toCents(rate * multiplier);

  return { quantity: 1, unitPrice: amount, amount };
}

/** `true` when a fee carries a positive finite amount. */
export function isBillableFee(
  fee: QuestionnaireFee | null | undefined,
): fee is QuestionnaireFee {
  return (
    !!fee &&
    typeof fee.amount === 'number' &&
    Number.isFinite(fee.amount) &&
    fee.amount > 0
  );
}

/**
 * `true` when a form-level price override is set — a finite amount of `0` or
 * more. Unlike `isBillableFee`, `0` counts: the kill slot and processing fee
 * cards keep "empty" (no override, standard rate applies) apart from `0` (the
 * processor waived it on this form), so a stored `{ amount: 0 }` is a
 * deliberate free override, not a blank input.
 * @param fee The kill slot or processing fee as stored on the form.
 */
export function isSetFeeOverride(
  fee: QuestionnaireFee | null | undefined,
): fee is QuestionnaireFee {
  return (
    !!fee &&
    typeof fee.amount === 'number' &&
    Number.isFinite(fee.amount) &&
    fee.amount >= 0
  );
}

/**
 * The processing (cut & wrap) fee a questionnaire sets, if any.
 *
 * Reads `processingFee` and falls back to the legacy `pricePerPound` field —
 * forms saved before price types existed stored only a $/lb of hanging weight
 * there, so it reads back as `per_lb_hanging`. A `processingFee` of `0` is a
 * waiver (see `isSetFeeOverride`); a legacy `pricePerPound` of `0` meant unset.
 * @param questionnaire The questionnaire (or the fields of one) to read.
 */
export function questionnaireProcessingFee(
  questionnaire:
    | {
        processingFee?: QuestionnaireFee | null;
        pricePerPound?: number | null;
      }
    | null
    | undefined,
): QuestionnaireFee | undefined {
  if (!questionnaire) return undefined;
  if (isSetFeeOverride(questionnaire.processingFee)) {
    return questionnaire.processingFee;
  }
  if (isSetPrice(questionnaire.pricePerPound)) {
    return {
      amount: questionnaire.pricePerPound,
      priceType: 'per_lb_hanging',
    };
  }
  return undefined;
}

/** The candidate processing fees for one share, most specific first. */
export interface ProcessingFeeSources {
  /** The $/lb of hanging weight stamped on the questionnaire response. */
  responsePricePerPound?: number | null;
  /**
   * The answered questionnaire's processing fee — from
   * `questionnaireProcessingFee`, so a legacy `pricePerPound` is included.
   */
  questionnaireFee?: QuestionnaireFee | null;
  /** The processor's custom-cutsheet $/lb for the inspection level. */
  processorCustomRate?: number | null;
  /** A legacy V1 vendor cutsheet's own `pricePerPound`. */
  legacyCutsheetPricePerPound?: number | null;
}

/**
 * Resolves the processing (cut & wrap) fee for a share, in the order of
 * `resolveCutWrapPricePerPound`: a $/lb stamped on the response, then the
 * form's own processing fee (which may be fixed or $/lb live), then the
 * processor's hanging-weight rate, then a legacy V1 sheet's price. A form fee
 * of `0` is a deliberate waiver and wins over the processor's rate. Always
 * returns a fee — `amount` `0` when nothing is set, which callers surface as
 * a $0 line rather than dropping the charge.
 * @param sources The candidate prices for this share.
 */
export function resolveProcessingFee(
  sources: ProcessingFeeSources,
): QuestionnaireFee {
  if (isSetPrice(sources.responsePricePerPound)) {
    return {
      amount: sources.responsePricePerPound,
      priceType: 'per_lb_hanging',
    };
  }
  if (isSetFeeOverride(sources.questionnaireFee)) {
    return sources.questionnaireFee;
  }

  return {
    amount: resolveCutWrapPricePerPound({
      processorCustomRate: sources.processorCustomRate,
      legacyCutsheetPricePerPound: sources.legacyCutsheetPricePerPound,
    }),
    priceType: 'per_lb_hanging',
  };
}

/** A questionnaire's additional charge, as authored on the form. */
export interface QuestionnaireAdditionalCharge extends QuestionnaireFee {
  label: string;
  description?: string | null;
}

/** One additional charge, already resolved to what this share owes. */
export interface ResolvedAdditionalCharge extends ResolvedFee {
  /** Line-item name (the charge's label). */
  label: string;
  /** Invoice line description — the label, plus the note when one is set. */
  description: string;
}

/**
 * Resolves a questionnaire's additional charges into the lines one share owes.
 *
 * Fixed `per_animal` charges cover the whole head and are prorated by the
 * share's portion — a $100 fee on an animal cut into halves bills $50 to each
 * side, and a head that is only half sold therefore collects $50 and never the
 * rest. Fixed `per_split` charges bill in full on every share. Per-pound
 * charges bill the share's own weight (see `resolveFeeForSplit`).
 *
 * Charges that aren't a positive finite amount are dropped. Negative amounts
 * in particular are not passed through as credit lines — nothing validates the
 * sign on the write path, and the sibling `animalHead.invoice.additionalCharges`
 * consumer already guards the same way.
 * @param charges The charges authored on the questionnaire.
 * @param context The share being billed and its weights.
 */
export function resolveAdditionalCharges(
  charges: readonly QuestionnaireAdditionalCharge[] | null | undefined,
  context: SplitFeeContext,
): ResolvedAdditionalCharge[] {
  return (charges ?? []).filter(isBillableFee).map((charge) => ({
    ...resolveFeeForSplit(charge, context),
    label: charge.label,
    description: charge.description
      ? `${charge.label} — ${charge.description}`
      : charge.label,
  }));
}
